import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Home, Disc3, Library, Compass, Radio, Trophy, LogOut, ShieldCheck, Gift } from "lucide-react";
import { resolveImg } from "@/lib/api";
import MetazikIcon from "./MetazikIcon";
import SearchBar from "./SearchBar";
import NotificationBell from "./NotificationBell";
import ArtistOnboardingGuide from "./ArtistOnboardingGuide";
import AffiliateOfferModal from "./AffiliateOfferModal";

export default function Layout({ children }) {
  const { user, logout, isAdmin, isArtist } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const items = [
    { to: "/", label: t.nav.feed, icon: Home, testid: "nav-feed" },
    { to: "/store", label: t.nav.store, icon: Disc3, testid: "nav-store" },
    { to: "/library", label: t.nav.library, icon: Library, testid: "nav-library" },
    { to: "/challenges", label: t.nav.challenges, icon: Trophy, testid: "nav-challenges" },
    { to: "/lives", label: t.nav.lives, icon: Radio, testid: "nav-lives" },
  ];

  // Desktop-only: a thin "Discover" link kept in the header (not in bottom mobile nav).
  const desktopExtras = [
    { to: "/discover", label: t.nav.discover ?? "Découvrir", icon: Compass, testid: "nav-discover" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {user && isArtist && !user.onboarding_seen && <ArtistOnboardingGuide />}
      {user && <AffiliateOfferModal />}
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <MetazikIcon className="w-9 h-9 drop-shadow-[0_0_14px_rgba(255,0,80,0.45)]" />
            <span className="text-xl font-black font-display tracking-tight text-white">
              Metazik
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-6">
            {[...items, ...desktopExtras].map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <it.icon className="w-4 h-4" />
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <SearchBar />
            {user && <NotificationBell />}
            {user && ["artist", "viewer"].includes(user.role) && (
              <NavLink
                to="/affiliation"
                className={({ isActive }) => `p-2 rounded-full transition-colors ${isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
                data-testid="nav-affiliate"
                aria-label="Affiliation"
                title="Affiliation — mon code promo"
              >
                <Gift className="w-5 h-5" />
              </NavLink>
            )}
            {user ? (
              <>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="btn-ghost inline-flex items-center gap-1.5 text-xs"
                    data-testid="nav-admin"
                  >
                    <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">Admin</span>
                  </Link>
                )}
                <Link
                  to={`/profile/${user.id}`}
                  className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-white/5 transition-colors"
                  data-testid="nav-profile"
                >
                  <Avatar src={user.avatar} name={user.name} />
                  <span className="hidden sm:inline text-sm font-semibold text-white">{user.name}</span>
                </Link>
                <button
                  onClick={() => { logout(); navigate("/auth"); }}
                  className="btn-ghost"
                  data-testid="logout-btn"
                  aria-label="logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link to="/auth" className="btn-primary text-sm" data-testid="header-login-btn">
                {t.nav.login}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 pb-32 pt-4 sm:pt-6">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-20 left-2 right-2 z-40 glass-nav rounded-2xl border border-white/5">
        <div className="grid grid-cols-5">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`m-${it.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2 text-[10px] transition-colors ${isActive ? "text-white" : "text-zinc-400"}`
              }
            >
              <it.icon className="w-5 h-5" />
              <span className="truncate max-w-full">{it.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function Avatar({ src, name, size = "sm" }) {
  const sizes = {
    lg: "w-20 h-20 text-2xl",
    md: "w-12 h-12 text-base",
    sm: "w-8 h-8 text-xs",
  };
  const sz = sizes[size] || sizes.sm;
  if (src) return <img src={resolveImg(src)} alt={name} className={`${sz} rounded-full object-cover border border-white/10`} />;
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={`${sz} rounded-full grid place-items-center font-bold text-white bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]`}>
      {initial}
    </div>
  );
}
